import React from 'react';
import { RotateCcwIcon } from 'lucide-react';
import { RecoveryRules, defaultRecoveryRules, recoveryChannels, rulesMatchDefaults } from '../data/recoveryRules';
type SubmissionRulesProps = {
  batchName: string;
  rules: RecoveryRules;
  onChange: (rules: RecoveryRules) => void;
  onReset: () => void;
};
export function SubmissionRules({
  batchName,
  rules,
  onChange,
  onReset
}: SubmissionRulesProps) {
  const inherited = rulesMatchDefaults(rules);
  const toggleChannel = (id: string) => onChange({
    ...rules,
    channels: rules.channels.includes(id) ? rules.channels.filter(channel => channel !== id) : [...rules.channels, id]
  });
  return <section className="rounded-2xl border border-[#BB5E40]/20 bg-cream/50 p-5" aria-label={`Recovery rules for ${batchName}`} data-id="element-2140" data-mp="2icyn05itzk9n">
      <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-2141" data-mp="29elezvm0eygw">
        <div data-id="element-2142" data-mp="29gpl8k7ds9kc">
          <h3 className="text-sm font-semibold" data-id="element-2143" data-mp="2ed6ka9g4c8c7">Recovery rules for {batchName}</h3>
          <p className="mt-1 text-xs text-ink/55" data-id="element-2144" data-mp="2ef96eie5hhbn">
            {inherited ? 'Inheriting your account defaults from Settings. Any change here applies to this upload only.' : 'Custom rules for this upload. Your account defaults are unchanged.'}
          </p>
        </div>
        {!inherited && <button type="button" onClick={onReset} className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3 py-1.5 text-xs font-semibold text-ink/65 transition-colors hover:bg-paper" data-id="element-2145" data-mp="31df211k3vjx7">
            <RotateCcwIcon className="h-3 w-3" aria-hidden="true" data-id="element-2146" data-mp="28r2l7mca611o" />
            Reset to defaults
          </button>}
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-2147" data-mp="2inp78egcey5a">
        <div data-id="element-2148" data-mp="2ilchhf4cfjho">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-2149" data-mp="1uxuxi5rblpp9">Approved channels</p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-2150" data-mp="1pz1ha5knfkle">
            {recoveryChannels.map(channel => {
            const active = rules.channels.includes(channel.id);
            return <button key={channel.id} type="button" role="switch" aria-checked={active} onClick={() => toggleChannel(channel.id)} className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${active ? 'border-[#BB5E40] bg-[#BB5E40] text-cream' : 'border-ink/15 bg-paper text-ink/60 hover:border-[#BB5E40]/40'}`} data-id="element-2151" data-mp="3v044dkiylc3z">
                  {channel.iconUrl && <span aria-hidden="true" className={`h-3.5 w-3.5 shrink-0 ${active ? 'bg-cream' : 'bg-ink/60'}`} style={{
                WebkitMaskImage: `url(${channel.iconUrl})`,
                maskImage: `url(${channel.iconUrl})`,
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain'
              }} data-id="element-2152" data-mp="3g4y135b8zrec" />}
                  {channel.name}
                </button>;
          })}
          </div>
          {rules.channels.length === 0 && <p className="mt-3 text-xs font-medium text-[#94452E]" data-id="element-2153" data-mp="mpifznxu4zct">Select at least one channel or this upload cannot be activated.</p>}
        </div>

        <div className="space-y-5" data-id="element-2154" data-mp="ht1fdu9g8siu">
          <RuleSlider label="Index price" detail="Starting list price as a share of MSRP." value={rules.indexPrice} min={20} max={100} suffix="% of MSRP" onChange={indexPrice => onChange({
          ...rules,
          indexPrice
        })} data-id="element-2155" data-mp="dpvc8vhmvhgl" />
          <RuleSlider label="Sales horizon" detail="How long this upload stays in market." value={rules.horizonMonths} min={2} max={12} suffix="months" onChange={horizonMonths => onChange({
          ...rules,
          horizonMonths,
          liveSellingAfterMonth: Math.min(rules.liveSellingAfterMonth, horizonMonths)
        })} data-id="element-2156" data-mp="2f6b9lrk3ybel" />
          <RuleSlider label="Hold live selling until" detail="Live selling moves units faster but recovers less." value={rules.liveSellingAfterMonth} min={0} max={rules.horizonMonths} suffix={rules.liveSellingAfterMonth === 0 ? 'available immediately' : 'months in'} onChange={liveSellingAfterMonth => onChange({
          ...rules,
          liveSellingAfterMonth
        })} data-id="element-2157" data-mp="1wfbe07i3y3cc" />
        </div>
      </div>
    </section>;
}
function RuleSlider({
  label,
  detail,
  value,
  min,
  max,
  suffix,
  onChange
}: {
  label: string;
  detail: string;
  value: number;
  min: number;
  max: number;
  suffix: string;
  onChange: (value: number) => void;
}) {
  const id = `rule-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return <div data-id="element-2158" data-mp="17w9q9nb2y4il">
      <div className="flex items-baseline justify-between gap-3" data-id="element-2159" data-mp="17wrsd0kfbcyf">
        <label htmlFor={id} className="text-sm font-semibold" data-id="element-2160" data-mp="13t3gw6so7x7w">{label}</label>
        <span className="text-sm font-semibold tabular-nums text-[#94452E]" data-id="element-2161" data-mp="1hteb9jt6vtxt">
          {value}
          <span className="ml-1 text-xs font-medium text-ink/50" data-id="element-2162" data-mp="1cub6m0h4fpqd">{suffix}</span>
        </span>
      </div>
      <input id={id} type="range" min={min} max={max} value={value} onChange={event => onChange(Number(event.target.value))} className="mt-2 w-full accent-[#BB5E40]" data-id="element-2163" data-mp="1rj7jqby1bsxu" />
      <p className="mt-1 text-xs text-ink/50" data-id="element-2164" data-mp="20kxenrr4la0d">{detail}</p>
    </div>;
}
export { defaultRecoveryRules };